import React, { Component, ComponentType } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { SortDirection } from '@syncfusion/ej2-grids';
import { Tabs, Tab } from 'react-bootstrap';
import { RootState } from '../../store';
import { IWebinarsListContainerStateProps, IWebinarsListContainerDispatchProps } from '../../interfaces/props/IWebinarsListProps';
import * as webinarsListStore from '../../store/modules/webinarsList.store';
import * as listGridStore from '../../store/modules/listGrid.store';
import { URL } from '../../common/constants';
import { DATE_MOMENT_FORMAT } from '../../common/constants/advancedFilter.constants';
import { IMyRule } from '../../common/model/myRule.model';
import { FilterMode } from '../../common/model/enumerations/filterMode.model';
import { RuleCondition } from '../../common/model/enumerations/ruleCondition.model';
import { RuleOperator } from '../../common/model/enumerations/ruleOperator.model';
import * as dateHelpers from '../../helpers/date.helpers';
import { dataManagerService, oDataService, queryBuilderService } from '../../services';
import WebinarsListFilter from './webinarsListFilter/webinarsListFilter';
import WebinarsGrid from './webinarsGrid/webinarsGrid';

type WebinarsListContainerPropsType = PropsFromRedux & RouteComponentProps & WithTranslation;

class WebinarsListContainer extends Component<WebinarsListContainerPropsType> {
  public webinarsDataManager: DataManager = null;
  public webinarsQuery: Query = new Query()
    .select(['id', 'name', 'speakers', 'date', 'description', 'videoUrl', 'active']);

  public constructor(props: WebinarsListContainerPropsType) {
    super(props);

    this.webinarsDataManager = dataManagerService.buildDataManager(URL.ODATA.WEBINAR_FILTERS);

    this.props.reset();
  }

  public componentWillUnmount() {
    this.props.reset();
  }

  public onFilterButtonClick() {
    this.props.setCurrentPage(webinarsListStore.Type.SET_CURRENT_PAGE, 1);
    this.fetchWebinars();
  }

  public onCurrentPageChange(currentPage: number) {
    this.props.setCurrentPage(webinarsListStore.Type.SET_CURRENT_PAGE, currentPage);
    this.fetchWebinars();
  }

  public onPageSizeChange(pageSize: number) {
    this.props.setPageSize(webinarsListStore.Type.SET_PAGE_SIZE, pageSize);
    this.fetchWebinars();
  }

  public onSortingChange(field: string, direction: SortDirection) {
    this.props.setSorting(webinarsListStore.Type.SET_SORTING, field, direction);
    this.fetchWebinars();
  }

  public render() {
    const {
      webinars,
      mode,
      filter,
      isFetching,
      searchDate,
      currentPage,
      pageSize,
      totalCount,
      sortField,
      sortDirection,
      setFilterName,
      setFilterSpeakers,
      setFilterDate,
      setFilterDescription,
      setFilterVideoUrl,
      setFilterActive,
      i18n
    } = this.props;

    return (
      <div>
        <div className='row'>
          <div className='col'>
            <h2>{i18n.t('webinarsList.title')}</h2>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <div className='mb-3'>
              <Tabs id='webinarsListTabs' className='mb-3'>
                <Tab eventKey={FilterMode.SIMPLE} title={i18n.t('enums.filterMode.SIMPLE')}>
                  <WebinarsListFilter
                    filter={filter}
                    listMode={mode}
                    isFetching={isFetching}
                    onNameChange={setFilterName}
                    onSpeakersChange={setFilterSpeakers}
                    onDateChange={setFilterDate}
                    onDescriptionChange={setFilterDescription}
                    onVideoUrlChange={setFilterVideoUrl}
                    onActiveChange={setFilterActive}
                    onFilterButtonClick={() => this.onFilterButtonClick()}
                  />
                </Tab>
              </Tabs>
            </div>
            <WebinarsGrid
              webinars={webinars}
              listMode={mode}
              isFetching={isFetching}
              searchDate={searchDate}
              currentPage={currentPage}
              pageSize={pageSize}
              totalCount={totalCount}
              sortField={sortField}
              sortDirection={sortDirection}
              onCurrentPageChange={(cp: number) => this.onCurrentPageChange(cp)}
              onPageSizeChange={(ps: number) => this.onPageSizeChange(ps)}
              onSortingChange={(field: string, direction: SortDirection) => this.onSortingChange(field, direction)}
            />
          </div>
        </div>
      </div>
    );
  }

  private fetchWebinars() {
    const { isFetching, fetchWebinars } = this.props;

    if (!isFetching) {
      fetchWebinars(oDataService, this.webinarsDataManager, this.buildQuery());
    }
  }

  private buildQuery(): Query {
    const { filter } = this.props;

    const myRule: IMyRule = {
      condition: RuleCondition.AND,
      myRules: []
    };
    if (filter.name) {
      myRule.myRules.push({
        field: 'name',
        operator: RuleOperator.CONTAINS,
        value: filter.name,
        ignoreCase: true
      });
    }
    if (filter.speakers) {
      myRule.myRules.push({
        field: 'speakers',
        operator: RuleOperator.CONTAINS,
        value: filter.speakers,
        ignoreCase: true
      });
    }
    if (filter.date.length) {
      myRule.myRules.push({
        field: 'date',
        operator: RuleOperator.GREATER_THAN_OR_EQUAL,
        value: dateHelpers.toDateWithoutZones(filter.date[0], DATE_MOMENT_FORMAT),
        isDate: true
      });
      myRule.myRules.push({
        field: 'date',
        operator: RuleOperator.LESS_THAN_OR_EQUAL,
        value: dateHelpers.toDateWithoutZones(filter.date[1], DATE_MOMENT_FORMAT),
        isDate: true
      });
    }
    if (filter.description) {
      myRule.myRules.push({
        field: 'description',
        operator: RuleOperator.CONTAINS,
        value: filter.description,
        ignoreCase: true
      });
    }
    if (filter.videoUrl) {
      myRule.myRules.push({
        field: 'videoUrl',
        operator: RuleOperator.CONTAINS,
        value: filter.videoUrl,
        ignoreCase: true
      });
    }
    if (filter.active && filter.active != null) {
      myRule.myRules.push({
        field: 'active',
        operator: RuleOperator.EQUAL,
        value: filter.active
      });
    }

    return queryBuilderService.buildQuery(this.webinarsDataManager, myRule, this.webinarsQuery.clone());
  }
}

const mapStateToProps = (state: RootState): IWebinarsListContainerStateProps => ({
  webinars: state.webinarsListStore.webinars,
  mode: state.webinarsListStore.mode,
  isChangingMode: state.webinarsListStore.isChangingMode,
  filter: state.webinarsListStore.filter,
  isFetching: state.webinarsListStore.grid.isFetching,
  searchDate: state.webinarsListStore.grid.searchDate,
  currentPage: state.webinarsListStore.grid.currentPage,
  pageSize: state.webinarsListStore.grid.pageSize,
  totalCount: state.webinarsListStore.grid.totalCount,
  sortField: state.webinarsListStore.grid.sortField,
  sortDirection: state.webinarsListStore.grid.sortDirection
});

const mapDispatchToProps: IWebinarsListContainerDispatchProps = {
  reset: webinarsListStore.reset,
  resetHard: webinarsListStore.resetHard,
  setMode: webinarsListStore.setMode,
  setIsChangingMode: webinarsListStore.setIsChangingMode,
  setFilterName: webinarsListStore.setFilterName,
  setFilterSpeakers: webinarsListStore.setFilterSpeakers,
  setFilterDate: webinarsListStore.setFilterDate,
  setFilterDescription: webinarsListStore.setFilterDescription,
  setFilterVideoUrl: webinarsListStore.setFilterVideoUrl,
  setFilterActive: webinarsListStore.setFilterActive,
  setCurrentPage: listGridStore.setCurrentPage,
  setPageSize: listGridStore.setPageSize,
  setSorting: listGridStore.setSorting,
  fetchWebinars: webinarsListStore.fetchWebinars
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(
  withRouter,
  withTranslation(),
  connector
)(WebinarsListContainer) as ComponentType;
